import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { regionRisk } from "@frontier/content/risk";
import type { ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";
import { galaxyDestination, galaxyRegionAt, type GalaxyTarget } from "./galaxy-navigation";
import type { GameController } from "./game/mount-game";
import { NAVIGATION_SNAPSHOT_MAX_AGE_MS } from "./game/navigation-types";
import type { NavigationState } from "./game/navigation-types";

interface Course {
  playerId: string;
  runId: string;
  sessionKey: string;
  target: GalaxyTarget;
  revision: number;
}

export function useGalaxyCourse({ playerId, runId, sessionKey, world, pilot,
  connected, ready, blocked, controller, onJump, receivedAt, gateFailure }: {
  playerId: string;
  runId: string;
  sessionKey: string;
  world: WorldSnapshot | null;
  pilot: ShipSnapshot | undefined;
  receivedAt: number;
  gateFailure: number;
  connected: boolean;
  ready: boolean;
  blocked: boolean;
  controller: RefObject<Pick<GameController, "navigate"> | null>;
  onJump: (gateId: string, riskAcknowledged: boolean) => void;
}) {
  const [saved, setSaved] = useState<{ playerId: string; target: GalaxyTarget } | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const revision = useRef(0);
  const leg = useRef<{ key: string; gateId: string | null; attempted: boolean; failure: number } | null>(null);
  const select = useCallback((target: GalaxyTarget | null) => {
    setSaved(target && playerId ? { playerId, target } : null);
    if (!target) { leg.current = null; setCourse(null); }
  }, [playerId]);
  const cancel = useCallback(() => { leg.current = null; setCourse(null); }, []);
  const plot = useCallback((target: GalaxyTarget) => {
    if (!playerId || !runId || !sessionKey) return;
    select(target);
    setCourse({ playerId, runId, sessionKey, target, revision: ++revision.current });
  }, [playerId, runId, sessionKey, select]);
  const navigationChanged = useCallback((state: NavigationState) => {
    if (state.status === "blocked" || (state.status === "idle" &&
      (state.reason === "manual" || state.reason === "cancelled" || state.reason === "stale"))) cancel();
  }, [cancel]);

  useEffect(() => {
    if (!course) return;
    if (course.playerId !== playerId || course.runId !== runId ||
      course.sessionKey !== sessionKey || pilot?.dead || pilot?.docked) {
      cancel();
      return;
    }
    if (!connected || !ready || blocked || !world || !pilot || pilot.pending ||
      performance.now() - receivedAt > NAVIGATION_SNAPSHOT_MAX_AGE_MS) return;
    const regionId = galaxyRegionAt(world, pilot)?.id;
    if (!regionId || !controller.current) return;
    const step = `${course.revision}:${regionId}`;
    if (leg.current?.key !== step) {
      const route = galaxyDestination(world, pilot, course.target);
      if (!route) {
        cancel();
        return;
      }
      leg.current = { key: step, gateId: route.gateId, attempted: false, failure: gateFailure };
      if (!controller.current.navigate(route.point)) { cancel(); return; }
    }
    const current = leg.current;
    if (current?.attempted && current.failure !== gateFailure) { cancel(); return; }
    if (!current || current.key !== step || !current.gateId || current.attempted) return;
    const gate = world.gates.find(item => item.id === current.gateId);
    if (!gate || Math.hypot(pilot.x - gate.x, pilot.y - gate.y) > gate.radius) return;
    // Bind the request to this leg before dispatch, including rejected or delayed replies.
    current.failure = gateFailure;
    current.attempted = true;
    onJump(gate.id, regionRisk(gate.toSectorId) === "open-pvp");
  }, [course, playerId, runId, sessionKey, pilot, world, connected, ready, blocked, controller, onJump, receivedAt, gateFailure, cancel]);

  return {
    selected: saved?.playerId === playerId ? saved.target : null,
    select, plot, cancel, navigationChanged,
  };
}
